import { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, CircularProgress, Divider, IconButton,
    useMediaQuery, useTheme,
} from '@mui/material';
import {
    Close as CloseIcon,
    BarChart as BarChartIcon,
    Refresh as RefreshIcon,
    FileDownloadOutlined as FileDownloadIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useExtractPersonQuery } from '../../../../hooks/QueryBuilder';
import { useTrackedQueryResult } from '../../../../hooks/QueryBuilder/useTrackedQueryResult';
import { getQueryResultTotal } from '../../../../hooks/QueryBuilder/useQueryResult';
import { useUser } from '../../../../context/UserContext';
import { useLanguage } from '../../../../context/LanguageContext';
import { CUSTOMER_ACTIONS, useRights } from '../../../../rights';
import QueryResultBody from './QueryResultBody';
import QueryExportDialog from './QueryExportDialog';

const EXPORT_KEY = 'pages.clients.queryBuilder.exportDialog';

interface QueryResultDialogProps {
    open: boolean;
    onClose: () => void;
    queryId: string | number | null;
    description?: string;
    starting?: boolean;
}

function QueryResultDialog({ open, onClose, queryId, description, starting = false }: QueryResultDialogProps) {
    const { t } = useLanguage();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { userInfos } = useUser();
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const { result, status, loading, error, relaunching, relaunchCount } = useTrackedQueryResult(open, queryId, starting);
    const { extractPersonQuery, loading: exporting, error: exportError, reset: resetExport } = useExtractPersonQuery();

    useEffect(() => {
        if (open) return;
        resetExport();
        setExportDialogOpen(false);
    }, [open, resetExport]);

    const title = result?.description || description;
    const total = getQueryResultTotal(result);
    const canRelaunchCount = !!queryId && !loading && (status === 'expired' || status === 'error');
    const { can } = useRights();
    // Extraction soumise à da.query.extract, et retirée par da.query.no_extract (comme l'API)
    const canExport = can(CUSTOMER_ACTIONS.extract) && !!queryId && !!result?.taskId && status === 'completed' && typeof total === 'number' && total > 0;

    const handleExport = async ({ notify, fields }: { notify: string[]; fields: string[] }) => {
        if (!queryId || !result?.taskId) return;
        const toastId = toast.loading(t(`${EXPORT_KEY}.requestInProgress`));
        try {
            await extractPersonQuery({ taskId: result.taskId, queryId, notify, fields });
            setExportDialogOpen(false);
            toast.update(toastId, { render: t(`${EXPORT_KEY}.requestSuccess`), type: 'success', isLoading: false, autoClose: 5000 });
        } catch (err) {
            toast.update(toastId, {
                render: err instanceof Error ? err.message : t(`${EXPORT_KEY}.requestError`),
                type: 'error',
                isLoading: false,
                autoClose: 5000,
            });
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BarChartIcon sx={{ color: 'primary.main' }} />
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            {t('pages.clients.queryBuilder.resultDialog.title')}
                            {title && ` : ${title}`}
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <Divider />

                <DialogContent sx={{ pt: 2, minHeight: 200 }}>
                    <QueryResultBody starting={starting} loading={loading} error={error} result={result} status={status} />
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2, justifyContent: canRelaunchCount || canExport ? 'space-between' : 'flex-end' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {canRelaunchCount && (
                            <Button
                                onClick={relaunchCount}
                                variant="contained"
                                size="small"
                                startIcon={relaunching ? <CircularProgress color="inherit" size={16} /> : <RefreshIcon />}
                                disabled={relaunching}
                            >
                                {t(relaunching ? 'pages.clients.queryBuilder.resultDialog.relaunching' : 'pages.clients.queryBuilder.resultDialog.relaunchCount')}
                            </Button>
                        )}
                        {canExport && (
                            <Button onClick={() => setExportDialogOpen(true)} variant="contained" size="small" startIcon={<FileDownloadIcon />}>
                                {t('pages.clients.queryBuilder.resultDialog.export')}
                            </Button>
                        )}
                    </Box>
                    <Button onClick={onClose} variant="outlined" size="small">
                        {t('common.close')}
                    </Button>
                </DialogActions>
            </Dialog>

            <QueryExportDialog
                open={exportDialogOpen}
                defaultEmail={userInfos?.email || ''}
                totalCount={result?.count}
                loading={exporting}
                error={exportError}
                onClose={() => setExportDialogOpen(false)}
                onConfirm={handleExport}
            />
        </>
    );
}

export default QueryResultDialog;
